import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import AdminTable from "components/admin/AdminTable";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { formatDate, formatNumber } from "utils/format";
import { dialogProps, useSubmit } from "layouts/players/PlayerActions";

/** 1 Pet Coin = 100đ (docs/design/98-nap-tien.md mục 9). */
const VND_PER_COIN = 100;
const MAX_AMOUNT = 1000000;
const MIN_REASON = 5;

/**
 * Cộng / trừ Pet Coin (tiền nạp thật) của tài khoản. Quyền riêng petcoin.give, bắt buộc lý do,
 * mỗi lần ghi một dòng vào sổ Pet Coin. Người đang online thấy số mới trên HUD ngay.
 */
export function PetCoinDialog({ userId, onClose, onDone, balance }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [subtract, setSubtract] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [countAsRecharge, setCountAsRecharge] = useState(true);
  const [viaMail, setViaMail] = useState(true);
  const [mailTitle, setMailTitle] = useState("");
  const [mailContent, setMailContent] = useState("");
  const value = Math.abs(Math.trunc(Number(amount) || 0));
  const mail = !subtract && viaMail;
  const after = subtract ? balance - value : balance + (mail ? 0 : value);
  const reasonOk = reason.trim().length >= MIN_REASON;
  const invalid = value <= 0 || value > MAX_AMOUNT || !reasonOk || after < 0;
  return (
    <ActionDialog
      open
      title={subtract ? "Trừ Pet Coin" : "Cộng Pet Coin"}
      description="Pet Coin là tiền nạp thật của tài khoản (1 Pet Coin = 100đ). Cộng mặc định gửi vào thư, người chơi bấm nhận thì Pet Coin mới vào tài khoản và ghi sổ. Trừ thì trừ thẳng. Lý do và tên admin ghi vào lịch sử."
      color={subtract ? "error" : "success"}
      submitText={subtract ? "Trừ" : mail ? "Gửi thư" : "Cộng"}
      busy={busy}
      disabled={invalid}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/petcoin`, {
            amount: subtract ? -value : value,
            reason: reason.trim(),
            countAsRecharge: !subtract && countAsRecharge,
            viaMail: mail,
            mailTitle: mail ? mailTitle.trim() : "",
            mailContent: mail ? mailContent.trim() : "",
          })
        )
      }
    >
      <MDTypography variant="button">
        Đang có: <strong>{formatNumber(balance)} Pet Coin</strong>
      </MDTypography>
      <FormControlLabel
        control={<Switch checked={subtract} onChange={(e) => setSubtract(e.target.checked)} />}
        label="Trừ thay vì cộng"
      />
      <MDInput
        type="number"
        label="Số Pet Coin"
        value={amount}
        inputProps={{ min: 1, max: MAX_AMOUNT, step: 1 }}
        helperText={
          value > 0
            ? `≈ ${formatNumber(value * VND_PER_COIN)}đ. ${
                mail
                  ? "Số dư chưa đổi cho tới khi người chơi nhận thư"
                  : `Sau khi ${subtract ? "trừ" : "cộng"}: ${formatNumber(after)} Pet Coin`
              }`
            : `Tối đa ${formatNumber(MAX_AMOUNT)} Pet Coin mỗi lần`
        }
        error={value > MAX_AMOUNT || after < 0}
        onChange={(e) => setAmount(e.target.value)}
      />
      <MDInput
        label="Lý do (bắt buộc)"
        placeholder="Ví dụ: chuyển khoản MB 18/10 ref FT2629... ghi sai nội dung"
        value={reason}
        multiline
        minRows={2}
        error={reason.length > 0 && !reasonOk}
        helperText={
          reasonOk
            ? " "
            : `Ít nhất ${MIN_REASON} ký tự. Nên ghi mã giao dịch ngân hàng nếu cộng cho lần nạp lỗi`
        }
        onChange={(e) => setReason(e.target.value)}
      />
      {!subtract && (
        <FormControlLabel
          control={
            <Switch
              checked={countAsRecharge}
              onChange={(e) => setCountAsRecharge(e.target.checked)}
            />
          }
          label="Tính vào tổng nạp (bỏ chọn nếu là quà tặng, bồi thường)"
        />
      )}
      {!subtract && (
        <FormControlLabel
          control={<Switch checked={viaMail} onChange={(e) => setViaMail(e.target.checked)} />}
          label="Gửi qua thư, người chơi tự nhận (tắt để cộng thẳng vào số dư)"
        />
      )}
      {mail && (
        <>
          <MDInput
            label="Tiêu đề thư (để trống dùng mặc định)"
            placeholder={value > 0 ? `Nhận ${formatNumber(value)} Pet Coin` : "Nhận ... Pet Coin"}
            value={mailTitle}
            onChange={(e) => setMailTitle(e.target.value)}
          />
          <MDInput
            label="Nội dung thư người chơi thấy (để trống dùng mặc định)"
            value={mailContent}
            multiline
            minRows={2}
            onChange={(e) => setMailContent(e.target.value)}
          />
          <Alert severity="info">
            Thư không hết hạn và không bị tự xoá khi chưa nhận. Lý do ở trên chỉ ghi vào lịch sử
            admin, người chơi không thấy.
          </Alert>
        </>
      )}
      {after < 0 && <Alert severity="error">Tài khoản không đủ Pet Coin để trừ.</Alert>}
    </ActionDialog>
  );
}

PetCoinDialog.propTypes = {
  ...dialogProps,
  balance: PropTypes.number.isRequired,
};

const SOURCE_COLORS = {
  1: "success",
  2: "warning",
  3: "default",
  4: "info",
  6: "secondary",
  7: "primary",
};

/** Tab Pet Coin: số dư, tổng nạp, sổ Pet Coin của tài khoản (mới nhất trước). */
export function PetCoinTab({ userId, reloadKey }) {
  const [page, setPage] = useState(1);
  const { data, loading, error } = useFetch(
    () => api.get(`/api/admin/players/${userId}/petcoin`, { page, size: 50 }),
    [userId, page, reloadKey]
  );
  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      {data && (
        <MDBox px={2} pt={2} display="flex" gap={3} flexWrap="wrap">
          <MDTypography variant="button">
            Số dư: <strong>{formatNumber(data.balance)} Pet Coin</strong> (≈{" "}
            {formatNumber(data.balance * VND_PER_COIN)}đ)
          </MDTypography>
          <MDTypography variant="button">
            Tổng nạp: <strong>{formatNumber(data.total)} Pet Coin</strong>
          </MDTypography>
          <MDTypography variant="button">
            Đang chờ trong thư: <strong>{formatNumber(data.pendingTotal)} Pet Coin</strong>
            {data.pending?.length > 0 ? ` (${data.pending.length} thư chưa nhận)` : ""}
          </MDTypography>
        </MDBox>
      )}
      {data?.pending?.length > 0 && (
        <MDBox px={2} pt={1}>
          {data.pending.map((m) => (
            <MDTypography key={m.mailId} variant="caption" display="block" color="text">
              Thư #{m.mailId} &quot;{m.title}&quot;: {formatNumber(m.amount)} Pet Coin, gửi{" "}
              {formatDate(m.time)}
            </MDTypography>
          ))}
        </MDBox>
      )}
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        rowKey="id"
        page={page}
        size={50}
        hasMore={data?.hasMore}
        onPageChange={setPage}
        emptyText="Chưa có biến động Pet Coin"
        columns={[
          { key: "time", label: "Thời gian", width: 170, render: (r) => formatDate(r.time) },
          {
            key: "delta",
            label: "Thay đổi",
            align: "right",
            render: (r) => (
              <MDTypography variant="button" color={r.delta > 0 ? "success" : "error"}>
                {r.delta > 0 ? "+" : ""}
                {formatNumber(r.delta)}
              </MDTypography>
            ),
          },
          {
            key: "balance",
            label: "Số dư sau",
            align: "right",
            render: (r) => formatNumber(r.balance),
          },
          {
            key: "source",
            label: "Nguồn",
            render: (r) => (
              <Chip
                size="small"
                color={SOURCE_COLORS[r.source] || "default"}
                label={r.sourceName}
              />
            ),
          },
          { key: "note", label: "Ghi chú", wrap: true },
        ]}
      />
    </>
  );
}

PetCoinTab.defaultProps = { reloadKey: 0 };

PetCoinTab.propTypes = {
  userId: PropTypes.number.isRequired,
  reloadKey: PropTypes.number,
};
